/**
 * FinanceAnomalies — panel de anomalías detectadas (F-28).
 *
 * Principios de UX:
 * - Si no hay anomalías: empty state sobrio, sin verdes chillones.
 * - high/medium: cada una se lista individualmente.
 * - low (datos incompletos): se agrupan en una sola fila expandible para no
 *   comerse visualmente a las de severidad mayor.
 * - Botón "Recalcular" fuerza bypass de la caché (60s) del backend.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Section } from "@/pages/Shell";
import {
  WarningOctagon, WarningCircle, Info, ArrowClockwise, CaretRight, CaretDown, Sparkle,
} from "@phosphor-icons/react";

const SEVERITY_META = {
  high: {
    color: "#b85c3e",
    bg: "bg-[#fbe8df]",
    text: "text-[#7a2c11]",
    border: "border-[#e8a685]",
    Icon: WarningOctagon,
    label: "HIGH",
  },
  medium: {
    color: "#d4a34a",
    bg: "bg-[#fbf3df]",
    text: "text-[#7a5a14]",
    border: "border-[#e9ce84]",
    Icon: WarningCircle,
    label: "MEDIUM",
  },
  low: {
    color: "#9b9892",
    bg: "bg-stone-50",
    text: "text-stone-600",
    border: "border-stone-200",
    Icon: Info,
    label: "LOW",
  },
};

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function FinanceAnomalies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [expandedLow, setExpandedLow] = useState(false);

  const load = async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/finance/anomalies${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(false); }, []);

  const groups = useMemo(() => {
    const items = data?.items || [];
    const high = items.filter(i => i.severity === "high");
    const medium = items.filter(i => i.severity === "medium");
    const low = items.filter(i => i.severity === "low");
    return { high, medium, low };
  }, [data]);

  const total = data?.total ?? 0;

  return (
    <Section
      title={total > 0 ? `Anomalías detectadas (${total})` : "Anomalías detectadas"}
      subtitle="Revisión automática por reglas: duplicados, gasto atípico, datos incompletos y desviaciones de presupuesto."
    >
      {loading && (
        <div className="dv-card text-sm text-stone-500 text-center py-4" data-testid="anomalies-loading">
          Analizando…
        </div>
      )}
      {err && <div className="dv-card text-sm text-red-700">{err}</div>}

      {!loading && !err && data && (
        <div className="dv-card" data-testid="anomalies-panel">
          {/* Header: severity counts + recalcular */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 text-xs">
              <SeverityPill severity="high" count={data.by_severity.high} />
              <SeverityPill severity="medium" count={data.by_severity.medium} />
              <SeverityPill severity="low" count={data.by_severity.low} />
            </div>
            <div className="flex items-center gap-3 text-xs text-stone-400">
              <span>Última revisión: {fmtWhen(data.generated_at)}</span>
              <button
                onClick={() => load(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-1 hover:text-stone-900"
                data-testid="anomalies-refresh"
              >
                <ArrowClockwise className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Recalculando…" : "Recalcular"}
              </button>
            </div>
          </div>

          {/* Empty state */}
          {total === 0 && (
            <div className="flex items-center gap-3 py-6 text-sm text-stone-500" data-testid="anomalies-empty">
              <Sparkle className="w-4 h-4 text-stone-400" />
              Sin anomalías detectadas. Todo en orden.
            </div>
          )}

          {/* High */}
          {groups.high.map((a) => (
            <AnomalyRow key={a.entity_id + a.type} anomaly={a} />
          ))}
          {/* Medium */}
          {groups.medium.map((a) => (
            <AnomalyRow key={a.entity_id + a.type} anomaly={a} />
          ))}

          {/* Low — agrupadas */}
          {groups.low.length > 0 && (
            <div className="mt-1 border-t border-stone-100 pt-2" data-testid="anomalies-low-group">
              <button
                onClick={() => setExpandedLow(!expandedLow)}
                className="w-full flex items-center gap-2 py-2 text-sm text-stone-600 hover:text-stone-900"
                data-testid="anomalies-low-toggle"
              >
                {expandedLow ? <CaretDown size={12} /> : <CaretRight size={12} />}
                <Info className="w-4 h-4 text-stone-400" />
                <span>
                  {groups.low.length} {groups.low.length === 1
                    ? "factura con datos incompletos"
                    : "facturas con datos incompletos"}
                </span>
              </button>
              {expandedLow && (
                <div className="pl-6 pb-2 space-y-1">
                  {groups.low.map((a) => (
                    <div
                      key={a.entity_id}
                      className="text-xs text-stone-600 py-1"
                      data-testid={`anomaly-low-${a.entity_id}`}
                    >
                      <span className="text-stone-500">{a.entity_id}</span>
                      <span className="text-stone-400 mx-1.5">·</span>
                      {a.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function SeverityPill({ severity, count }) {
  const m = SEVERITY_META[severity];
  if (count === 0) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${m.bg} ${m.text} ${m.border}`}
      data-testid={`anomalies-pill-${severity}`}
    >
      <span className="uppercase tracking-wider text-[10px]">{m.label}</span>
      <span className="font-semibold tabular-nums">{count}</span>
    </span>
  );
}

function AnomalyRow({ anomaly }) {
  const m = SEVERITY_META[anomaly.severity] || SEVERITY_META.low;
  const Icon = m.Icon;
  return (
    <div
      className="flex items-start gap-3 py-3 border-t border-stone-100 first:border-t-0"
      data-testid={`anomaly-${anomaly.type}-${anomaly.entity_id}`}
    >
      <div className="shrink-0 mt-0.5">
        <Icon className={`w-4 h-4 ${m.text}`} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${m.bg} ${m.text} ${m.border}`}>
            {m.label}
          </span>
          <span className="text-sm font-medium text-stone-900">{anomaly.title}</span>
        </div>
        <div className="text-sm text-stone-600 mt-0.5">{anomaly.description}</div>
      </div>
    </div>
  );
}
